import {
  AceInvitationRequest,
  InvitationCustomerRequest,
  InvitationProjectRequest,
  InvitationPayloadValue,
} from './types/AceInvitationRequest.types';
import { getRandomAwsContacts } from './data/awsContacts';
import { getRandomCompanyPersona } from './data/companyPersonas';
import {
  generateEngagementTitle,
  getBusinessProblemForIndustry,
} from './data/projectData';
import {
  CountryCode,
  Industry,
  PayloadType,
  ReceiverResponsibility,
} from './types/types';
import {
  randomFromArray,
  randomDateInRange,
  expirationDateInRange,
  generateRandomAmount,
  randomAwsAccountId,
} from './utils/utils';

const RECEIVER_RESPONSIBILITIES: ReceiverResponsibility[] = [
  'Software Partner',
  'Services Partner',
  'Managed Services Provider',
  'Training Partner',
  'Co-Sell Facilitator',
];

const COUNTRY_CODES: CountryCode[] = [
  'US',
  'GB',
  'DE',
  'FR',
  'CA',
  'AU',
  'JP',
  'ES',
];

// Generate a random customer based on a company persona
export const generateRandomCustomer = (): InvitationCustomerRequest => {
  const persona = getRandomCompanyPersona();
  let countryCode: CountryCode;

  // Use preferred country code if available (required for Government = US)
  if (
    persona.preferredCountryCodes &&
    persona.preferredCountryCodes.length > 0
  ) {
    countryCode = randomFromArray(persona.preferredCountryCodes);
  } else {
    countryCode = randomFromArray(COUNTRY_CODES);
  }

  return {
    companyName: persona.companyName,
    countryCode,
    industry: persona.industry,
    websiteUrl: persona.websiteUrl,
  };
};

// Generate a random project for a specific customer
export const generateRandomProject = (
  customer: InvitationCustomerRequest,
): InvitationProjectRequest => {
  return {
    businessProblem: getBusinessProblemForIndustry(
      customer.industry as Industry,
    ),
    expectedCustomerSpend: [
      {
        amount: generateRandomAmount(),
        currencyCode: 'USD',
        frequency: 'Monthly',
        targetCompany: customer.companyName,
      },
    ],
    targetCompletionDate: randomDateInRange(12),
    title: generateEngagementTitle(
      customer.companyName,
      customer.industry as Industry,
    ),
  };
};

// Generate a complete random invitation payload
export const generateRandomInvitation = (): AceInvitationRequest => {
  const customer = generateRandomCustomer();

  // Prepare the payload value
  const payloadValue: InvitationPayloadValue = {
    receiverResponsibilities: [
      randomFromArray<ReceiverResponsibility>(RECEIVER_RESPONSIBILITIES),
    ],
    customer,
    project: generateRandomProject(customer),
    senderContacts: getRandomAwsContacts(),
  };

  return {
    engagementTitle: generateEngagementTitle(
      customer.companyName,
      customer.industry as Industry,
    ),
    expirationDate: expirationDateInRange(),
    invitationDate: new Date().toISOString(),
    payload: {
      value: payloadValue,
    },
    payloadType: 'OpportunityInvitation' as PayloadType,
    senderAwsAccountId: randomAwsAccountId(),
  };
};
